import { FunctionComponent, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

import {
	faCheck,
	faClock,
	faHeart,
	faHeartCrack,
	faUserSlash,
} from "@fortawesome/pro-solid-svg-icons";
import { faChevronDown } from "@fortawesome/pro-regular-svg-icons";

import { ProspectInterest } from "@/enum/prospect.enum";
import { Icon } from "@/components";
import { classNames } from "@/utils/classNames";
import { useUpdateConversationSentimentMutation } from "@/pages/Private/redux/conversation/conversation.api";
import { Conversation } from "@/pages/Private/pages/Apollo/schema/get-people";
import { useViewportClampedPanel } from "@/utils/useViewportClampedPanel";
import { getComposedEventTarget } from "@/utils/getComposedEventTarget";

// ─── Config ────────────────────────────────────────────────────────────────────

// System-derived sentiments (OUT_OF_OFFICE / MEANINGLESS / UNSUBSCRIBED) are intentionally absent:
// they are not manually assignable here, so the map is Partial and the dropdown only offers its keys.
const SENTIMENT_CONFIG: Partial<
	Record<ProspectInterest, { icon: typeof faHeart; textClass: string; bgClass: string }>
> = {
	[ProspectInterest.INTERESTED]: {
		icon: faHeart,
		textClass: "text-status-success",
		bgClass: "bg-status-success-light",
	},
	[ProspectInterest.NOT_INTERESTED]: {
		icon: faHeartCrack,
		textClass: "text-status-error",
		bgClass: "bg-status-error-light",
	},
	[ProspectInterest.NEUTRAL]: {
		icon: faCheck,
		textClass: "text-status-info",
		bgClass: "bg-status-info-light",
	},
	[ProspectInterest.BAD_TIMING]: {
		icon: faClock,
		textClass: "text-status-warning",
		bgClass: "bg-status-warning-light",
	},
	[ProspectInterest.WRONG_PERSON]: {
		icon: faUserSlash,
		textClass: "text-status-wrong-person",
		bgClass: "bg-status-wrong-person-light",
	},
};

const MANUAL_SENTIMENTS = Object.keys(SENTIMENT_CONFIG) as ProspectInterest[];

const toLabel = (s: ProspectInterest) => s.replaceAll("_", " ").capitalizeString();

// ─── Component ─────────────────────────────────────────────────────────────────

export const ProspectSentimentDropdown: FunctionComponent<{
	conversation?: Conversation;
}> = ({ conversation }) => {
	const [isOpen, setIsOpen] = useState(false);
	const [updateSentiment, { isLoading }] = useUpdateConversationSentimentMutation();

	const triggerRef = useRef<HTMLDivElement>(null);
	const menuRef = useRef<HTMLDivElement>(null);

	// Keeps the portalled panel inside the viewport wherever the trigger sits (PREC-4138).
	useViewportClampedPanel(menuRef, isOpen);

	const current = conversation?.sentiment ?? ProspectInterest.NEUTRAL;
	const cfg = SENTIMENT_CONFIG[current] ?? SENTIMENT_CONFIG[ProspectInterest.NEUTRAL]!;

	// Close on outside click
	useEffect(() => {
		if (!isOpen) {
			return;
		}

		const handleClick = (e: MouseEvent) => {
			const target = getComposedEventTarget(e);

			if (!triggerRef.current?.contains(target) && !menuRef.current?.contains(target)) {
				setIsOpen(false);
			}
		};

		window.addEventListener("mousedown", handleClick);

		return () => window.removeEventListener("mousedown", handleClick);
	}, [isOpen]);

	// Portal position
	const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});

	useEffect(() => {
		if (!isOpen || !triggerRef.current) {
			return;
		}

		const rect = triggerRef.current.getBoundingClientRect();
		const spaceBelow = window.innerHeight - rect.bottom;
		const menuHeight = 220;

		setMenuStyle({
			position: "fixed",
			left: rect.left,
			top: spaceBelow >= menuHeight ? rect.bottom + 4 : rect.top - menuHeight - 4,
			minWidth: rect.width,
			zIndex: 9999,
		});
	}, [isOpen]);

	const handleSelect = async (s: ProspectInterest) => {
		if (s === current || isLoading) {
			return;
		}

		setIsOpen(false);

		await updateSentiment({
			id: conversation?.id ?? -2,
			sentiment: s,
		}).unwrap();
	};

	return (
		<>
			{/* Trigger badge */}
			<div
				ref={triggerRef}
				className={classNames(
					"inline-flex h-6 items-center gap-1 w-fit text-xs font-medium rounded-lg px-2 cursor-pointer select-none transition-opacity",
					cfg.bgClass,
					cfg.textClass,
					isLoading && "opacity-50 pointer-events-none"
				)}
				onClick={(e) => {
					e.stopPropagation();
					setIsOpen((v) => !v);
				}}
			>
				<Icon className="w-3 h-3" icon={cfg.icon} />
				{toLabel(current)}
				<Icon className="w-2.5 h-2.5 opacity-60" icon={faChevronDown} />
			</div>

			{/* Dropdown menu via portal */}
			{isOpen &&
				ReactDOM.createPortal(
					<div
						ref={menuRef}
						className="flex flex-col gap-0.5 p-1 bg-white border border-gray-200 rounded-lg shadow-lg"
						style={menuStyle}
					>
						{MANUAL_SENTIMENTS.map((s) => {
							const c = SENTIMENT_CONFIG[s]!;
							const isSelected = s === current;

							return (
								<button
									key={s}
									className={classNames(
										"flex items-center hover:cursor-pointer gap-2 px-2 py-1.5 rounded-md text-xs font-medium w-full text-left transition-colors",
										c.bgClass,
										c.textClass,
										isSelected ? "ring-1 ring-inset ring-current" : "opacity-70 hover:opacity-100"
									)}
									onClick={(e) => {
										e.stopPropagation();
										handleSelect(s);
									}}
								>
									<Icon className="w-3 h-3 flex-shrink-0" icon={c.icon} />
									{toLabel(s)}
									{isSelected && <Icon className="w-3 h-3 ml-auto flex-shrink-0" icon={faCheck} />}
								</button>
							);
						})}
					</div>,
					document.body
				)}
		</>
	);
};
